"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toMarkerAreaState = void 0;
var ImageInfo_1 = require("../../ImageInfo");
var toCropAreaState_1 = require("./toCropAreaState");
var round_1 = require("../../round");
var colorValues = {
    red: '#C1280B',
    orange: '#FF9024',
    yellow: '#F3E00E',
    green: '#16DC81',
    lightBlue: '#1BB1E9',
    blue: '#2343E8',
    darkBlue: '#2343E8',
    violet: '#DC54B7',
    pink: '#DC54B7',
    black: '#000000',
};
function toMarkerAreaState(imageInfo, defaultStrokeWidth) {
    var _a;
    var markers = (_a = imageInfo.markupState) === null || _a === void 0 ? void 0 : _a.annotations;
    if (!markers) {
        return null;
    }
    if ((0, ImageInfo_1.isNodeMarkupMarkersState)(markers)) {
        var cropAreaState_1 = (0, toCropAreaState_1.toCropAreaState)(imageInfo);
        return {
            height: (cropAreaState_1 === null || cropAreaState_1 === void 0 ? void 0 : cropAreaState_1.cropRect.height) || imageInfo.height,
            width: (cropAreaState_1 === null || cropAreaState_1 === void 0 ? void 0 : cropAreaState_1.cropRect.width) || imageInfo.width,
            markers: markers.data
                .map(function (nodeMarkupMarker) {
                if (nodeMarkupMarker.shape === 'circle') {
                    return fromNodeMarkupCircle(nodeMarkupMarker, cropAreaState_1, defaultStrokeWidth);
                }
                if (nodeMarkupMarker.shape === 'rectangle') {
                    return fromNodeMarkupRectangle(nodeMarkupMarker, cropAreaState_1, defaultStrokeWidth);
                }
                if (nodeMarkupMarker.shape === 'line') {
                    return fromNodeMarkupLine(nodeMarkupMarker, cropAreaState_1, defaultStrokeWidth);
                }
                if (nodeMarkupMarker.shape === 'arrow') {
                    return fromNodeMarkupArrow(nodeMarkupMarker, cropAreaState_1, defaultStrokeWidth);
                }
                if (nodeMarkupMarker.shape === 'gap') {
                    return fromNodeMarkupGap(nodeMarkupMarker, cropAreaState_1, defaultStrokeWidth);
                }
            })
                // Filter just in case there's an unknown shape type from bad data that would have
                // broken the markers editor.
                .filter(function (marker) { return marker; }),
        };
    }
    else if ((0, ImageInfo_1.isMarkerjsMarkersState)(markers)) {
        return fromMarkerjsState(markers, defaultStrokeWidth);
    }
    // Markers type isn't implemented!
    return null;
}
exports.toMarkerAreaState = toMarkerAreaState;
function fromMarkerjsState(state, defaultStrokeWidth) {
    return __assign(__assign({}, state.data), { markers: state.data.markers.map(function (marker) {
            // The stroke width is saved relative to the size of the image in the editor. We
            // recalculate the stroke width in case the editor now is being opened on a different
            // screen size.
            if (marker['strokeWidthRatioToDefault']) {
                marker['strokeWidth'] = (0, round_1.round)(defaultStrokeWidth * marker['strokeWidthRatioToDefault'], 1);
            }
            else if (marker['strokeWidth']) {
                // Fallback to the default if we can't determine which stroke width option was chosen
                marker['strokeWidth'] = defaultStrokeWidth;
            }
            return marker;
        }) });
}
function getCommonRectangleState(defaultStrokeWidth) {
    return {
        strokeWidth: defaultStrokeWidth,
        fillColor: 'transparent',
        opacity: 1,
        rotationAngle: 0,
        state: 'select',
        notes: undefined,
        strokeDasharray: '',
        containerTransformMatrix: {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            e: 0,
            f: 0,
        },
        visualTransformMatrix: {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            e: 0,
            f: 0,
        },
    };
}
function fromNodeMarkupCircle(nodeMarkupCircle, cropAreaState, defaultStrokeWidth) {
    var cropOffset = getCropOffset(cropAreaState);
    return __assign(__assign({}, getCommonRectangleState(defaultStrokeWidth)), { typeName: 'EllipseFrameMarker', height: nodeMarkupCircle.radius * 2, width: nodeMarkupCircle.radius * 2, left: nodeMarkupCircle.x - cropOffset.x - nodeMarkupCircle.radius, top: nodeMarkupCircle.y - cropOffset.y - nodeMarkupCircle.radius, strokeColor: colorValues[nodeMarkupCircle.color] });
}
function fromNodeMarkupRectangle(nodeMarkupRectangle, cropAreaState, defaultStrokeWidth) {
    var cropOffset = getCropOffset(cropAreaState);
    return __assign(__assign({}, getCommonRectangleState(defaultStrokeWidth)), { typeName: 'FrameMarker', height: nodeMarkupRectangle.height, width: nodeMarkupRectangle.width, left: nodeMarkupRectangle.x - cropOffset.x, top: nodeMarkupRectangle.y - cropOffset.y, strokeColor: colorValues[nodeMarkupRectangle.color] });
}
function fromNodeMarkupLine(nodeMarkupLine, cropAreaState, defaultStrokeWidth) {
    return __assign(__assign({}, getBaseLineStateFromNodeMarkup(nodeMarkupLine, cropAreaState, defaultStrokeWidth)), { typeName: 'LineMarker' });
}
function fromNodeMarkupArrow(nodeMarkupArrow, cropAreaState, defaultStrokeWidth) {
    return __assign(__assign({}, getBaseLineStateFromNodeMarkup(nodeMarkupArrow, cropAreaState, defaultStrokeWidth)), { typeName: 'ArrowMarker', arrowType: 'end' });
}
function fromNodeMarkupGap(nodeMarkupGap, cropAreaState, defaultStrokeWidth) {
    return __assign(__assign({}, getBaseLineStateFromNodeMarkup(nodeMarkupGap, cropAreaState, defaultStrokeWidth)), { typeName: 'MeasurementMarker' });
}
function getBaseLineStateFromNodeMarkup(nodeMarkupLine, cropAreaState, defaultStrokeWidth) {
    var cropOffset = getCropOffset(cropAreaState);
    return {
        x1: nodeMarkupLine.xStart - cropOffset.x,
        x2: nodeMarkupLine.xEnd - cropOffset.x,
        y1: nodeMarkupLine.yStart - cropOffset.y,
        y2: nodeMarkupLine.yEnd - cropOffset.y,
        strokeColor: colorValues[nodeMarkupLine.color],
        strokeWidth: defaultStrokeWidth,
        notes: undefined,
        state: 'select',
        strokeDasharray: '',
    };
}
function getCropOffset(cropAreaState) {
    var x = 0;
    var y = 0;
    if (cropAreaState) {
        // CropArea always has a 20px offset on x and y, but MarkerArea doesn't
        x = cropAreaState.cropRect.x - 20;
        y = cropAreaState.cropRect.y - 20;
    }
    return { x: x, y: y };
}
